import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import type { BusinessVerificationStatus } from '../../lib/schemas/business';

interface VerifiedMarkProps {
  /**
   * Null, or anything but VERIFIED, renders nothing.
   *
   * Typed from the schema rather than listing the states, so a state added to
   * the API — EXPIRED and REVOKED were — cannot quietly fail to reach here.
   */
  status: BusinessVerificationStatus | null | undefined;
}

/**
 * The check beside a verified business's name, in a feed or a comment thread.
 *
 * This is where the question "is this really them" actually gets asked — next
 * to something they wrote, not on a profile page nobody opened. A pharmacy
 * replying to a complaint about it is only worth reading if you can tell it is
 * the pharmacy.
 *
 * The full badge with its wording lives on the profile; this is its compact
 * form, and it keeps the same meaning: an identity was checked, nothing more.
 * It carries a label rather than relying on the icon alone.
 */
export function VerifiedMark({ status }: VerifiedMarkProps) {
  const { t } = useTranslation();

  if (status !== 'VERIFIED') return null;

  return (
    // The tooltip's text becomes the icon's accessible name — MUI puts it
    // there — so it has to say what this is as well as what it means. Two
    // sentences, one name, no way to hear only half of it.
    <Tooltip title={`${t('business.verified')} — ${t('business.verifiedHint')}`}>
      {/* titleAccess, not aria-label: MUI marks an icon aria-hidden unless it
          is given a title, so a label alone leaves it invisible to a screen
          reader — which is the half of the audience the badge matters most to. */}
      <VerifiedRoundedIcon
        fontSize="inherit"
        titleAccess={t('business.verified')}
        sx={{ color: 'business', verticalAlign: 'middle', ml: 0.25 }}
      />
    </Tooltip>
  );
}
